import { useEffect, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';

/**
 * Hook for anti-cheat fullscreen enforcement in quizzes
 * - Auto-enables fullscreen when quiz starts
 * - Tracks fullscreen exits
 * - Shows warning when user exits fullscreen
 * - Prevents split-screen on mobile
 */

interface UseFullscreenQuizOptions {
  enabled: boolean; // Whether fullscreen enforcement is enabled
  onExitFullscreen?: (exitCount: number) => void; // Callback when user exits fullscreen
  isUploading?: boolean; // Whether file upload is in progress (to ignore fullscreen exit)
}

interface UseFullscreenQuizReturn {
  isFullscreen: boolean;
  exitCount: number;
  requestFullscreen: () => Promise<void>;
  exitFullscreen: () => Promise<void>;
  showWarning: boolean;
  dismissWarning: () => void;
}

export function useFullscreenQuiz(options: UseFullscreenQuizOptions): UseFullscreenQuizReturn {
  const { enabled, onExitFullscreen } = options;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exitCount, setExitCount] = useState(0);
  // showWarning disabled - không hiện modal cảnh báo
  const hasAutoStarted = useRef(false);
  const lastExitTime = useRef<number>(0);
  const hasShownToast = useRef(false); // Chỉ hiện toast 1 lần duy nhất

  // Check if device supports fullscreen
  const supportsFullscreen = useCallback(() => {
    return !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
  }, []);

  // Check if currently in fullscreen
  const checkFullscreen = useCallback(() => {
    return !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullScreenElement
    );
  }, []);

  // Request fullscreen
  const requestFullscreen = useCallback(async () => {
    if (!supportsFullscreen()) {
      console.warn('Fullscreen API not supported');
      return;
    }

    try {
      const elem = document.documentElement;

      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if ((elem as any).webkitRequestFullscreen) {
        await (elem as any).webkitRequestFullscreen();
      } else if ((elem as any).mozRequestFullScreen) {
        await (elem as any).mozRequestFullScreen();
      } else if ((elem as any).msRequestFullscreen) {
        await (elem as any).msRequestFullscreen();
      }

      setIsFullscreen(true);
      console.log('🖥️ Fullscreen enabled for quiz');
    } catch (error) {
      console.error('Failed to enter fullscreen:', error);
      // Toast đã bị tắt để tránh phiền nhiễu
    }
  }, [supportsFullscreen]);

  // Exit fullscreen
  const exitFullscreen = useCallback(async () => {
    try {
      // Check if actually in fullscreen before trying to exit
      const isActuallyInFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullScreenElement
      );

      if (!isActuallyInFullscreen) {
        setIsFullscreen(false);
        return;
      }

      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }

      setIsFullscreen(false);
      console.log('🖥️ Fullscreen disabled');
    } catch (error) {
      console.error('Failed to exit fullscreen:', error);
    }
  }, []);

  // Dismiss warning - DISABLED (không dùng nữa)
  const dismissWarning = useCallback(() => {
    // Do nothing - modal đã bị tắt
  }, []);

  // Handle fullscreen change
  useEffect(() => {
    if (!enabled) return;

    const handleFullscreenChange = () => {
      const inFullscreen = checkFullscreen();
      setIsFullscreen(inFullscreen);

      // Set hasAutoStarted khi VÀO fullscreen thành công lần đầu
      if (inFullscreen && !hasAutoStarted.current) {
        hasAutoStarted.current = true;
        console.log('✅ Entered fullscreen - auto-submit on exit enabled');
        return; // Không xử lý gì thêm khi vào lần đầu
      }

      // User exited fullscreen → NỘP BÀI NGAY
      if (!inFullscreen && hasAutoStarted.current) {
        console.log('🚨 [useFullscreenQuiz] Fullscreen exited!');
        console.log('📊 [useFullscreenQuiz] Debug info:', {
          isUploading: options.isUploading,
          hasAutoStarted: hasAutoStarted.current,
          inFullscreen,
          enabled: options.enabled
        });

        // ✅ Ignore if uploading file
        if (options.isUploading) {
          console.log('✅ [useFullscreenQuiz] IGNORING fullscreen exit - Upload in progress');
          return;
        }

        const now = Date.now();

        // Debounce: only trigger once
        if (now - lastExitTime.current > 1000) {
          lastExitTime.current = now;
          console.warn('⚠️ User exited fullscreen → AUTO SUBMIT');

          // Callback để nộp bài
          if (onExitFullscreen) {
            onExitFullscreen(1); // Gọi với count = 1 để trigger auto submit
          }
        }
      }
    };

    // Listen to fullscreen change events (cross-browser)
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [enabled, checkFullscreen, requestFullscreen, onExitFullscreen, options.isUploading]);

  // Auto-start fullscreen when quiz begins
  useEffect(() => {
    if (enabled && !hasAutoStarted.current && supportsFullscreen()) {
      console.log('🚀 Auto-starting fullscreen...');

      // Small delay to ensure component is mounted
      const timer = setTimeout(() => {
        console.log('🔄 Requesting fullscreen now...');
        requestFullscreen();
        // Toast sẽ hiện khi vào fullscreen thành công (trong handleFullscreenChange)
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [enabled, supportsFullscreen, requestFullscreen]);

  // Detect visibility change (mobile split-screen, picture-in-picture)
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.warn('⚠️ Page is hidden (possible split-screen or PiP)');

        // Count as exit attempt on mobile
        if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          const now = Date.now();
          // Debounce: only count if more than 1 second since last exit
          if (now - lastExitTime.current > 1000) {
            // ⚠️ Set lastExitTime NGAY để chặn các events tiếp theo
            lastExitTime.current = now;

            setExitCount(prev => {
              const newCount = prev + 1;
              console.warn(`⚠️ Mobile: Page hidden - Count: ${newCount}/3`);

              // KHÔNG hiện toast - chỉ đếm im lặng

              if (onExitFullscreen) {
                onExitFullscreen(newCount);
              }

              return newCount;
            });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, onExitFullscreen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Exit fullscreen when quiz is unmounted
      if (isFullscreen) {
        exitFullscreen();
      }
    };
  }, [isFullscreen, exitFullscreen]);

  return {
    isFullscreen,
    exitCount,
    requestFullscreen,
    exitFullscreen,
    showWarning: false, // LUÔN false - không hiện modal
    dismissWarning
  };
}

